import { Link } from "@tanstack/react-router"
import { ArrowRight, Menu } from "lucide-react"

import { ServiceBrand } from "@/components/service-brand"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import type { Viewer } from "@/lib/domain"
import { viewerDefaultPath } from "@/lib/domain"

const publicNav = [
  { label: "About", hash: "#about" },
  { label: "Imagery", hash: "#imagery" },
  { label: "How it works", hash: "#workflow" },
  { label: "Service guide", hash: "#service-guide" },
]

export function PublicHeader({ viewer }: { viewer: Viewer | null }) {
  return (
    <header className="border-b border-border bg-white">
      <div className="mx-auto flex min-h-[82px] max-w-[1440px] items-center justify-between gap-6 px-4 sm:px-6 lg:px-10">
        <ServiceBrand />

        <nav
          className="hidden items-center gap-1 lg:flex"
          aria-label="Public navigation"
        >
          {publicNav.map((item) => (
            <a
              key={item.hash}
              href={item.hash}
              className="rounded-[10px] px-3 py-2 text-sm font-semibold text-[var(--ink)] transition-colors hover:bg-[var(--cloud)] hover:text-[var(--service-blue)] focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-[var(--cyan)]"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 sm:flex">
          {viewer ? (
            <>
              <span className="mr-1 hidden text-xs text-muted-foreground xl:inline">
                Signed in as {viewer.shortName}
              </span>
              <Button render={<Link to={viewerDefaultPath(viewer)} />}>
                Open workspace
                <ArrowRight />
              </Button>
            </>
          ) : (
            <>
              <Button render={<Link to="/login" />} variant="ghost">
                Sign in
              </Button>
              <Button render={<Link to="/login" />}>
                Open demo portal
                <ArrowRight />
              </Button>
            </>
          )}
        </div>

        <Sheet>
          <SheetTrigger
            render={
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="lg:hidden"
                aria-label="Open navigation"
              />
            }
          >
            <Menu />
          </SheetTrigger>
          <SheetContent side="right" className="w-[min(90vw,360px)] p-0">
            <SheetHeader className="border-b border-border px-5 py-5">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <ServiceBrand />
            </SheetHeader>
            <nav
              className="grid gap-1 px-4 py-5"
              aria-label="Mobile navigation"
            >
              {publicNav.map((item) => (
                <SheetClose
                  key={item.hash}
                  render={
                    <a
                      href={item.hash}
                      className="rounded-[10px] px-3 py-3 text-sm font-semibold text-[var(--ink)] hover:bg-[var(--cloud)]"
                    />
                  }
                >
                  {item.label}
                </SheetClose>
              ))}
            </nav>
            <div className="mt-auto border-t border-border p-4">
              <SheetClose
                render={
                  <Button
                    render={
                      <Link
                        to={viewer ? viewerDefaultPath(viewer) : "/login"}
                      />
                    }
                    className="w-full"
                  />
                }
              >
                {viewer ? "Open workspace" : "Open demo portal"}
                <ArrowRight />
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  )
}
